import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { api } from '../services/api'

export type GeolocationStatus =
  | 'idle'
  | 'loading'
  | 'granted'
  | 'denied'
  | 'unavailable'

export interface Coords {
  lat: number
  lng: number
}

export interface CityInfo {
  prefecture: string
  city: string
}

interface GeolocationState {
  status: GeolocationStatus
  coords: Coords | null
  cityInfo: CityInfo | null
}

const CACHE_KEY = 'yokoso.geolocation.v1'
const CACHE_TTL_MS = 30 * 60 * 1000 // 30 minutes

interface CachedState {
  coords: Coords
  cityInfo: CityInfo | null
  savedAt: number
}

function readCache(): CachedState | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedState
    if (Date.now() - parsed.savedAt > CACHE_TTL_MS) return null
    return parsed
  } catch {
    return null
  }
}

function writeCache(coords: Coords, cityInfo: CityInfo | null) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ coords, cityInfo, savedAt: Date.now() }),
    )
  } catch {
    // sessionStorage unavailable — ignore
  }
}

/**
 * Demo 用 URL パラメータから現在地を偽装する（既存の ?balance= パターンと同じ）。
 * - ?lat=35.68&lng=139.77 : 緯度経度を直接指定
 * - ?city=神奈川県平塚市  : prefecture + city を直接指定（ペアで使う）
 * - ?prefecture=東京都&city=千代田区 : 個別指定
 */
function readDemoParams(): Partial<GeolocationState> | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const lat = params.get('lat')
  const lng = params.get('lng')
  const cityCombo = params.get('city')
  const prefecture = params.get('prefecture')

  if (lat && lng) {
    return {
      status: 'granted',
      coords: { lat: parseFloat(lat), lng: parseFloat(lng) },
      cityInfo: null,
    }
  }

  if (cityCombo) {
    // "神奈川県平塚市" のような結合文字列を都道府県/市区町村に分割
    const match = cityCombo.match(/^(.+?[都道府県])(.+)$/)
    if (match) {
      return {
        status: 'granted',
        coords: null,
        cityInfo: { prefecture: match[1], city: match[2] },
      }
    }
  }

  if (prefecture && params.get('city')) {
    return {
      status: 'granted',
      coords: null,
      cityInfo: { prefecture, city: params.get('city')! },
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// 現在地は**画面ごとではなくアプリで 1 つ**持つ。
//
// 以前は画面ごとに state を持っていたため、「探す」で位置を取っても、既に開いて
// いる HOME はその回では知りようがなかった (次に開き直すまで「地域が分からない」
// ままで、HOME の入口が別の地域のイベントを出していた)。取得はモジュールで束ね、
// 購読している画面すべてに同じ結果を配る。
// ---------------------------------------------------------------------------

function initialState(): GeolocationState {
  const demo = readDemoParams()
  if (demo) return { status: 'granted', coords: null, cityInfo: null, ...demo }
  const cache = readCache()
  if (cache) return { status: 'granted', coords: cache.coords, cityInfo: cache.cityInfo }
  return { status: 'idle', coords: null, cityInfo: null }
}

let state: GeolocationState = initialState()
const listeners = new Set<() => void>()

function setState(next: GeolocationState) {
  state = next
  listeners.forEach((notify) => notify())
}

function subscribe(notify: () => void) {
  listeners.add(notify)
  return () => {
    listeners.delete(notify)
  }
}

/** 取得は同時に 1 回だけ (画面がいくつ開いていても getCurrentPosition は 1 回) */
let inFlight: Promise<void> | null = null

function acquire(): Promise<void> {
  if (inFlight) return inFlight
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    setState({ status: 'unavailable', coords: null, cityInfo: null })
    return Promise.resolve()
  }
  setState({ ...state, status: 'loading' })
  const started = new Promise<void>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords: Coords = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        let cityInfo: CityInfo | null = null
        try {
          const res = await api.reverseGeocode(coords.lat, coords.lng)
          if (res.prefecture && res.city) {
            cityInfo = { prefecture: res.prefecture, city: res.city }
          }
        } catch {
          // 逆ジオコード失敗: coords だけ保持して市区町村フィルタは無効化
        }
        writeCache(coords, cityInfo)
        setState({ status: 'granted', coords, cityInfo })
        resolve()
      },
      (err) => {
        setState({
          status: err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable',
          coords: null,
          cityInfo: null,
        })
        resolve()
      },
      { timeout: 10000, enableHighAccuracy: false, maximumAge: 10 * 60 * 1000 },
    )
  }).finally(() => {
    inFlight = null
  })
  inFlight = started
  return started
}

/** ブラウザで**既に許可済み**か。未回答 (prompt) と拒否は false */
async function alreadyGranted(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return false
    const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
    return status.state === 'granted'
  } catch {
    // Safari 等で geolocation の照会が使えない場合は「分からない」= 聞かない
    return false
  }
}

let autoTried = false

/**
 * **許可済みのときだけ**、入った直後に黙って取り直す。
 *
 * セッションをまたぐと保存した現在地は消える (sessionStorage) ので、ログアウト→
 * しばらく→ログイン、のように入り直すと毎回「地域が分からない」状態から始まり、
 * HOME の入口が別の地域のイベントを出していた。許可済みならダイアログは出ないので、
 * 入った時点で取り直して画面を揃える。
 *
 * **未回答の人には出さない** — 入った直後の許可ダイアログは断られやすく、断られると
 * 後から覆しにくい。位置が要る画面 (探す / 旅程 / スタンプ) が今までどおり聞く。
 */
function autoAcquireIfAlreadyGranted() {
  if (autoTried) return
  autoTried = true
  if (state.status !== 'idle') return // デモ偽装 / 保存済みの現在地がある
  void alreadyGranted().then((granted) => {
    if (granted && state.status === 'idle') void acquire()
  })
}

/**
 * 現在地を取得し、逆ジオコーディングで市区町村を解決するフック。
 * 取得失敗時 (denied / unavailable) は status 経由で通知し、UI 側で
 * 全件表示にフォールバックできるようにする。
 */
export function useGeolocation() {
  const snapshot = useSyncExternalStore(subscribe, () => state)

  useEffect(() => {
    autoAcquireIfAlreadyGranted()
  }, [])

  // Demo パラメータがあれば座標から市区町村を補完
  useEffect(() => {
    const demo = readDemoParams()
    if (!demo || !demo.coords || demo.cityInfo || state.cityInfo) return
    let cancelled = false
    api
      .reverseGeocode(demo.coords.lat, demo.coords.lng)
      .then((res) => {
        if (cancelled) return
        if (res.prefecture && res.city) {
          setState({ ...state, cityInfo: { prefecture: res.prefecture, city: res.city } })
        }
      })
      .catch(() => {
        /* noop */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const requestLocation = useCallback(() => {
    void acquire()
  }, [])

  return { ...snapshot, requestLocation }
}
